import React, { memo, useMemo } from 'react';
import { format, addHours, set, getHours } from 'date-fns';
import { getZIndexClass } from '@/lib/scheduling/z-index-layers';
import { useShiftHover } from '@/components/shifts/context/ShiftHoverProvider';

import { INTERVAL_MINUTES } from '@/lib/core/time-constants';
const MINUTES_PER_HOUR = 60;
const INTERVALS_PER_HOUR = MINUTES_PER_HOUR / INTERVAL_MINUTES;

interface TimeHeaderProps {
  totalDisplayHours: number;
  effectiveDisplayStartTime: Date;
  hideDayColumn?: boolean;
  show24Hours?: boolean; // Hide day boundary orange border when true
}

interface TimeIntervalData {
  intervalIndex: number;
  displayText: string;
  isHourMarker: boolean;
  isDayBoundary: boolean;
  gridColumnStart: number;
}

/**
 * Memoized individual time header cell
 * Only re-renders when its own hover state changes
 */
const TimeHeaderCell = memo<{
  interval: TimeIntervalData;
  isHovered: boolean;
  show24Hours: boolean;
  totalDisplayHours: number;
}>(({ interval, isHovered, show24Hours, totalDisplayHours }) => {
  const { intervalIndex, displayText, isHourMarker, isDayBoundary, gridColumnStart } = interval;

  return (
    <div
      key={`header-interval-${intervalIndex}`}
      className={`border-b dark:border-gray-600 text-xs text-center font-medium flex items-center justify-center sticky top-0 ${getZIndexClass('TIME_HEADER')} transition-colors duration-75 ${
        isHovered ? 'bg-gray-300/50 dark:bg-gray-700/50' : 'bg-transparent'
      } ${
        isDayBoundary && !show24Hours && totalDisplayHours > 24
          ? 'border-l-2 !border-l-orange-500 dark:!border-l-orange-400'
          : isHourMarker
            ? 'border-l'
            : ''
      }`}
      style={{
        gridColumn: gridColumnStart,
        gridRow: 1,
        minHeight: '40px',
        minWidth: 0,
        position: 'relative'
      }}
    >
      {/* Only render span for hour markers - no empty text node when false */}
      {isHourMarker && (
        <span style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%) rotate(-90deg)',
          whiteSpace: 'nowrap'
        }}>
          {displayText}
        </span>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if hover state or display properties change
  return (
    prevProps.isHovered === nextProps.isHovered &&
    prevProps.interval.intervalIndex === nextProps.interval.intervalIndex &&
    prevProps.show24Hours === nextProps.show24Hours &&
    prevProps.totalDisplayHours === nextProps.totalDisplayHours
  );
});

TimeHeaderCell.displayName = 'TimeHeaderCell';

const TimeHeader: React.FC<TimeHeaderProps> = ({
  totalDisplayHours,
  effectiveDisplayStartTime,
  hideDayColumn = false,
  show24Hours = false
}) => {
  // Get hovered time interval index from isolated hover context for highlighting
  const { hoveredTimeIntervalIndex } = useShiftHover();

  // Validate props to prevent rendering issues and cap overnight display at 12:00 next day
  const safeTotalDisplayHours = isNaN(totalDisplayHours) || totalDisplayHours <= 0 ? 12 : Math.min(totalDisplayHours, 36);
  const safeEffectiveDisplayStartTime = effectiveDisplayStartTime instanceof Date && !isNaN(effectiveDisplayStartTime.getTime())
    ? effectiveDisplayStartTime
    : new Date();

  // PERFORMANCE: Memoize time interval calculations - only recalculate when display parameters change
  // This prevents recalculating 96-144 interval objects on every hover
  // Using for loop instead of Array.from to reduce GC pressure
  const timeIntervals = useMemo((): TimeIntervalData[] => {
    const totalIntervals = Math.ceil(safeTotalDisplayHours * INTERVALS_PER_HOUR);
    const intervals: TimeIntervalData[] = [];

    for (let intervalIndex = 0; intervalIndex < totalIntervals; intervalIndex++) {
      const intervalMinutes = intervalIndex * INTERVAL_MINUTES;
      const hoursToAdd = Math.floor(intervalMinutes / MINUTES_PER_HOUR);
      const minutesToSet = intervalMinutes % MINUTES_PER_HOUR;

      const baseTime = addHours(set(new Date(safeEffectiveDisplayStartTime), { minutes: minutesToSet }), hoursToAdd);
      let displayText = '';

      const isHourMarker = minutesToSet === 0;

      if (isHourMarker) {
        const totalHours = getHours(safeEffectiveDisplayStartTime) + hoursToAdd;
        if (totalHours === 24) {
          displayText = '24:00';
        } else if (totalHours > 24) {
          const nextDayHour = totalHours - 24;
          if (nextDayHour > 12) {
            displayText = '12:00';
          } else {
            displayText = `${String(nextDayHour).padStart(2, '0')}:00`;
          }
        } else {
          displayText = format(baseTime, 'HH:mm');
        }
      }

      const totalHours = getHours(safeEffectiveDisplayStartTime) + hoursToAdd;
      const isDayBoundary = isHourMarker && (totalHours === 24 || displayText === '24:00');

      // Pre-calculate grid column to avoid recalculation during render
      const gridColumnStart = hideDayColumn ? intervalIndex + 1 : intervalIndex + 2;

      intervals.push({
        intervalIndex,
        displayText,
        isHourMarker,
        isDayBoundary,
        gridColumnStart
      });
    }

    return intervals;
  }, [safeTotalDisplayHours, safeEffectiveDisplayStartTime, hideDayColumn]);

  return (
    <>
      {/* Time Interval Headers - each cell is memoized to only re-render on hover change */}
      {timeIntervals.map((interval) => (
        <TimeHeaderCell
          key={`header-interval-${interval.intervalIndex}`}
          interval={interval}
          isHovered={hoveredTimeIntervalIndex === interval.intervalIndex}
          show24Hours={show24Hours}
          totalDisplayHours={safeTotalDisplayHours}
        />
      ))}
    </>
  );
};

export default memo(TimeHeader, (prevProps, nextProps) => {
  // Only re-render if display configuration changes
  return (
    prevProps.totalDisplayHours === nextProps.totalDisplayHours &&
    prevProps.effectiveDisplayStartTime.getTime() === nextProps.effectiveDisplayStartTime.getTime() &&
    prevProps.hideDayColumn === nextProps.hideDayColumn &&
    prevProps.show24Hours === nextProps.show24Hours
  );
});
